import { Button, SxProps, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Lightbulb as LightbulbIcon } from '@mui/icons-material';
import { WrappedPlanDefinition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PlanDefinition';
import { WrappedCondition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import { spreadSxProp } from 'src/utils/cssStyles';
import GoalConditionList from '../grid/GoalConditionList';
import Iconify from 'src/components/Iconify';
import uuidv4 from 'src/utils/uuidv4';
import { WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import GoalModal from '../grid/GoalModal';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedValueSet } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { WrappedCarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import { useState } from 'react';
import { ValueSetComposeIncludeConcept } from 'src/careflow/ph-core/fhir-client/resources';
import { CREATE_GOAL, UPDATE_GOAL, useEventBus } from 'src/event-bus';
import useGoalContext from 'src/careflow/care-coordination/case/goal/hook/useGoalContext';
import useClientGrid from 'src/careflow/ph-core/grid/hook/useClientGrid';

export type OnGoalSuggestionAction = (goalSuggestion: GoalSuggestion) => void;

export interface GoalSuggestion {
  addresses: (WrappedCondition | WrappedObservation)[];
  planDefinition: WrappedPlanDefinition;
}

export interface GoalSuggestionsGridProps {
  sx?: SxProps;
  goalSuggestions: GoalSuggestion[];
  carePlan: WrappedCarePlan | null;
  planDefinitions: WrappedPlanDefinition[];
  conditions: WrappedCondition[];
  observations: WrappedObservation[]
  patient: WrappedPatient | null;
  pathways: WrappedValueSet | null;
}

const GoalSuggestionsGrid = ({
  sx,
  goalSuggestions,
  carePlan,
  planDefinitions,
  conditions,
  observations,
  patient,
  pathways,
}: GoalSuggestionsGridProps) => {
  const { rowsPerPage, handlePageSizeChange, sortModel, handleSortModelChange } = useClientGrid({
    initialRowsPerPage: 5,
  });
  const { dispatchEvent } = useEventBus();
  const { isWorkflowContext } = useGoalContext();

  const [selectedGoalSuggestion, setSelectedGoalSuggestion] = useState<any>();
  const [isGoalModalOpen, setIsGoalModalOpen] = useState<boolean>(false);

  const onGoalSuggestionAction = (params: GoalSuggestion) => {
    setSelectedGoalSuggestion(params);
    setIsGoalModalOpen(true);
  };

  return (
    <>
      <DataGrid
        autoHeight
        getRowHeight={() => 'auto'}
        sx={[
          {
            marginTop: 1,
            height: 'auto',
            width: '100%',
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#f4f6f8',
            },
            '& .MuiDataGrid-cell': {
              border: 0,
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              maxHeight: '100px',
              overflowY: 'auto'
            },
            '& .MuiDataGrid-row': {
              borderBottom: '1px solid #EAECF0',
              '&:nth-of-type(odd)': {
                backgroundColor: '#FAFAFA',
              },
              '&:hover': {
                backgroundColor: '#F5F6F7',
              },
            },
          },
          ...spreadSxProp(sx),
        ]}
        rows={goalSuggestions}
        density="compact"
        getRowId={({ planDefinition }: GoalSuggestion) => 
          planDefinition?.id ?? uuidv4()
        }
        onRowDoubleClick={(params) => {
          onGoalSuggestionAction(params.row as GoalSuggestion)
        }}
        columns={getGoalSuggestionsGridColumns(onGoalSuggestionAction)}
        isRowSelectable={() => false}
        disableColumnFilter
        disableColumnMenu
        rowsPerPageOptions={[5, 10, 25]}
        pageSize={rowsPerPage}
        onPageSizeChange={handlePageSizeChange}
        pagination
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
      />

      <GoalModal
        planDefinitions={planDefinitions}
        conditions={conditions}
        observations={observations}
        goalToEdit={null}
        onSuccessfulEdit={() => dispatchEvent(UPDATE_GOAL, {isWorkflowContext})}
        onSuccessfulCreation={() => dispatchEvent(CREATE_GOAL, {isWorkflowContext})}
        defaultPathway={
          selectedGoalSuggestion?.planDefinition?.getGoalCategoryCoding?.() as ValueSetComposeIncludeConcept
        }
        pathways={(pathways?.asList?.() ?? []) as ValueSetComposeIncludeConcept[]}
        patient={patient}
        open={isGoalModalOpen}
        carePlan={carePlan}
        onClose={() => {
          setIsGoalModalOpen(false);
          setSelectedGoalSuggestion(null);
        }}
        defaultAddresses={selectedGoalSuggestion?.addresses}
        defaultPlanDefinition={selectedGoalSuggestion?.planDefinition}
      />
    </>
  );
};

export const getGoalSuggestionsGridColumns = (
  onGoalSuggestionAction: OnGoalSuggestionAction
): GridColDef[] => [
  {
    field: 'icon',
    headerName: '',
    align: 'center',
    flex: 0.1,
    renderCell: () => <LightbulbIcon htmlColor="#ffab00" />,
  },
  {
    field: 'goal',
    headerName: 'Goal',
    flex: 1,
    renderCell: (params) => {
      const { planDefinition } = params.row as GoalSuggestion;
      return (
        <Typography>
          {planDefinition?.getGoalDescription?.() ?? ''}
        </Typography>
      );
    },
  },
  {
    field: 'addressess',
    headerName: 'Addressess',
    flex: 1,
    renderCell: (params) => {
      const { addresses } = params.row as GoalSuggestion;

      const conditions = addresses.filter((x) => x.resourceType === 'Condition') as WrappedCondition[];
      const observations = addresses.filter((x) => x.resourceType === 'Observation') as WrappedObservation[];

      return <GoalConditionList conditions={conditions} observations={observations} />;
    },
  },
  {
    field: 'action',
    headerName: '',
    flex: 0.3,
    align: 'center',
    renderCell: (params) => {
      const goalSuggestion = params.row as GoalSuggestion;
      return (
        <Button
          sx={{ height: 34 }}
          disabled={false}
          onClick={() => {
            onGoalSuggestionAction(goalSuggestion);
          }}
          variant="contained"
          startIcon={<Iconify icon={'eva:plus-fill'} />}
        >
          Add
        </Button>
      );
    },
  },
];

export default GoalSuggestionsGrid;
